import { FormEvent, useState } from 'react';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import type { SendHandoffEmailResult } from '../../../../api/modules/deviceHandoff';
import { MOBILE_PX, mobilePx } from './mobileFallbackScale';
import { withAlpha } from './fallbackColor';

type FormStatus = 'idle' | 'submitting' | 'sent' | 'error';

interface EmailLinkFormProps {
  config: ResolvedDeviceFallbackConfig;
  onSubmit: (email: string) => Promise<SendHandoffEmailResult>;
}

// Deliberately permissive: the backend is the authority on deliverability, and this only
// needs to catch obvious typos before spending a request.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Only one of these renders at a time, and the screen hosts a single form, so a constant
// id is enough to point the input at its message for screen readers.
const EMAIL_ERROR_ID = 'handoff-email-error';

// Fixed rather than themed: the button sits on the amber token, and the stage colour that
// would otherwise supply this is free to be any darkness a campaign picks. Near-black with
// a trace of purple rather than pure black, which reads as a hole against the amber.
const SUBMIT_LABEL_COLOR = '#12081F';

/** Fill and frame for the field, as fractions of the accent. */
const FIELD_FILL_ALPHA = 0.08;
const FIELD_EDGE_ALPHA = 0.3;

/** Field and button heights are set by their padding, which only holds if the leading is. */
const CONTROL_LINE_HEIGHT = 1.2;

/**
 * What the visitor types, and the hint sitting behind it.
 *
 * Literal white rather than Tailwind's `text-white`, which is not white here: the app runs
 * `tailwindcss-themer`, and every theme redefines the `white` token — pale cyan by default,
 * near-black under the light theme. The typed address was rendering in that token and reading
 * as blue on the dark card. This field is painted on a colour the campaign controls, so the
 * text has to be stated outright rather than borrowed from a palette that moves.
 */
const FIELD_TEXT_COLOR = '#FFFFFF';

const ERROR_COPY: Record<'invalid_email' | 'rate_limited' | 'invalid_challenge' | 'error', string> = {
  invalid_email: 'That email doesn\u2019t look right. Mind checking it?',
  rate_limited: 'That\u2019s a few links already. Try again in a little while.',
  invalid_challenge: 'Couldn\u2019t send that just now. Give it another go.',
  error: 'Couldn\u2019t send that just now. Give it another go.',
};

export const EmailLinkForm = ({ config, onSubmit }: EmailLinkFormProps) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (status === 'submitting') return;

    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setStatus('error');
      setErrorMessage(ERROR_COPY.invalid_email);
      return;
    }

    setStatus('submitting');
    setErrorMessage(null);

    const result = await onSubmit(trimmed);
    if (result.ok) {
      setStatus('sent');
      return;
    }

    setStatus('error');
    setErrorMessage(ERROR_COPY[result.reason]);
  };

  if (status === 'sent') {
    return (
      <div className='flex flex-col items-center gap-2 text-center'>
        <p
          className='font-semibold'
          style={{ color: config.qrRimStartColor, fontSize: mobilePx(MOBILE_PX.sentConfirm) }}
        >
          Link Sent
        </p>
        <p style={{ color: config.copyColor, fontSize: mobilePx(MOBILE_PX.subcopy) }}>
          {config.mobileSentSubcopy}
        </p>
      </div>
    );
  }

  return (
    // noValidate: the native bubble would otherwise pre-empt submit and hide the styled
    // message below. `type="email"` stays only for the mobile keyboard it brings up.
    <form onSubmit={handleSubmit} noValidate className='flex flex-col' style={{ gap: mobilePx(MOBILE_PX.fieldGap) }}>
      <div className='flex flex-col' style={{ gap: mobilePx(MOBILE_PX.labelGap) }}>
        <label
          htmlFor='handoff-email'
          className='text-left'
          style={{ color: config.copyColor, fontSize: mobilePx(MOBILE_PX.fieldLabel) }}
        >
          {config.mobileEmailLabel}
        </label>
        <input
          id='handoff-email'
          type='email'
          inputMode='email'
          autoComplete='email'
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
            if (status === 'error') {
              setStatus('idle');
              setErrorMessage(null);
            }
          }}
          placeholder={config.mobileEmailPlaceholder}
          aria-invalid={status === 'error'}
          aria-describedby={errorMessage ? EMAIL_ERROR_ID : undefined}
          style={{
            fontSize: mobilePx(MOBILE_PX.fieldText),
            // Scaled rather than a fixed `py-2.5`, which stood the field a third taller
            // than the artboard once the type around it started tracking the viewport.
            paddingBlock: mobilePx(MOBILE_PX.fieldPaddingY),
            paddingInline: mobilePx(MOBILE_PX.fieldPaddingX),
            borderRadius: mobilePx(MOBILE_PX.fieldRadius),
            // The inherited `normal` runs near 1.5 here, which is most of the extra height
            // the padding was being blamed for.
            lineHeight: CONTROL_LINE_HEIGHT,
            color: FIELD_TEXT_COLOR,
            caretColor: FIELD_TEXT_COLOR,
            // Tinted with the accent rather than plain white alpha. White reads cold and
            // faintly blue against the purple card, which is what made the field look like
            // it belonged to a different screen.
            backgroundColor: withAlpha(config.accentColor, FIELD_FILL_ALPHA),
            border: `1px solid ${withAlpha(config.accentColor, FIELD_EDGE_ALPHA)}`,
          }}
          // Placeholder written as a literal for the same reason as the text colour, and
          // spelled out in full because Tailwind only generates classes it can read in the
          // source — an interpolated one would never be built.
          className='w-full placeholder:text-[rgba(255,255,255,0.35)] focus:outline-none focus:ring-2 focus:ring-white/20'
        />
      </div>

      {/* The amber carries the primary action on both fallback screens, where it is the QR
          rim. Black label rather than white: this is the one bright surface here, and
          white on amber does not hold. */}
      <button
        type='submit'
        disabled={status === 'submitting'}
        className='w-full font-bold transition-opacity disabled:opacity-60'
        style={{
          backgroundColor: config.qrRimStartColor,
          color: SUBMIT_LABEL_COLOR,
          fontSize: mobilePx(MOBILE_PX.submit),
          paddingBlock: mobilePx(MOBILE_PX.submitPaddingY),
          borderRadius: mobilePx(MOBILE_PX.submitRadius),
          lineHeight: CONTROL_LINE_HEIGHT,
        }}
      >
        {status === 'submitting' ? 'Sending\u2026' : config.mobileSubmitLabel}
      </button>

      {errorMessage && (
        <p
          id={EMAIL_ERROR_ID}
          role='alert'
          className='text-center text-red-300'
          style={{ fontSize: mobilePx(MOBILE_PX.quietAction) }}
        >
          {errorMessage}
        </p>
      )}
    </form>
  );
};
